<template>
 <div class="award-card-wrapper" @click="goToDetails">
   <!-- Main Card with Gradient Border -->
   <div class="award-card" :class="{ 'eligible-glow': isEligible }">
     <!-- Floating Badge for Status -->
     <div v-if="isEligible" class="status-badge eligible">
       <v-icon size="14" color="white">mdi-check-circle</v-icon>
       <span>Eligible</span>
     </div>
     <div v-else-if="isRedeemed" class="status-badge redeemed">
       <v-icon size="14" color="white">mdi-gift</v-icon>
       <span>Redeemed</span>
     </div>

     <!-- Header Section -->
     <div class="award-header">
       <div class="award-icon-container">
         <div class="award-icon-bg">
           <v-img
             :src="require('@/assets/svg/couponIcon.svg')"
             contain
             width="34"
             height="34"
             class="rounded-lg"
           ></v-img>
         </div>
         <div class="pulse-ring"></div>
       </div>
      
       <div class="award-info">
         <h3 class="award-title">{{ award.title }}</h3>
         <div class="award-meta">
           <div class="meta-item">
             <v-icon size="16" color="#6B7280">mdi-calendar</v-icon>
             <span>{{ formatDate(award.expiryDate) }}</span>
           </div>
           <div class="meta-item">
             <v-icon size="16" color="#6B7280">mdi-target</v-icon>
             <span>{{ award.goal_hours }} hrs goal</span>
           </div>
         </div>
       </div>

       <!-- Action Buttons -->
       <div v-if="award.issuedBy == profile._id" class="action-buttons">
         <v-btn
           icon
           class="action-btn edit-btn"
           @click.stop="onEdit"
           elevation="0"
         >
           <v-icon size="18">mdi-pencil</v-icon>
         </v-btn>
         <v-btn
           icon
           class="action-btn delete-btn"
           @click.stop="onDelete"
           elevation="0"
         >
           <v-icon size="18">mdi-delete</v-icon>
         </v-btn>
       </div>
     </div>

     <!-- Description Section -->
     <div class="description-section">
       <div class="description-content">
         <p v-if="text_collapsed" class="description-text">
           {{ collapsed_text }}
           <button @click.stop="text_collapsed = false" class="read-more-btn">
             read more
           </button>
         </p>
         <p v-else class="description-text">{{ award.description }}</p>
       </div>
     </div>

     <!-- Scratch Card Coupon Section -->
     <div
       v-if="showCouponCode && !couponRevealed"
       class="scratch-card-container"
       @click.stop
     >
       <div class="scratch-card-header">
         <div class="scratch-icon">
           <v-icon color="#FFD700" size="24">mdi-star</v-icon>
         </div>
         <span class="scratch-title">Scratch to reveal your coupon!</span>
       </div>
       
       <div class="scratch-card" ref="scratchCard">
         <canvas
           ref="scratchCanvas"
           class="scratch-canvas"
           @mousedown="startScratch"
           @mousemove="scratch"
           @mouseup="endScratch"
           @touchstart="startScratch"
           @touchmove="scratch"
           @touchend="endScratch"
         ></canvas>
         
         <!-- Hidden coupon content -->
         <div class="coupon-content">
           <div class="confetti-animation" v-if="showConfetti">
             <div class="confetti" v-for="n in 50" :key="n"></div>
           </div>
           <div class="coupon-celebration">
             <v-icon color="#FFD700" size="32">mdi-trophy</v-icon>
             <h3 class="congratulations">Congratulations!</h3>
             <div class="discount-badge">{{ award.discount || '50% OFF' }}</div>
             <p class="coupon-description">Your exclusive reward</p>
           </div>
           <div class="coupon-code-reveal" @click="copyCouponCode">
             <span class="code-label">Coupon Code:</span>
             <div class="code-display">
               <span class="code-text">{{ award.couponCode }}</span>
               <v-icon size="16" :color="copied ? '#22C55E' : '#6B7280'">
                 {{ copied ? 'mdi-check' : 'mdi-content-copy' }}
               </v-icon>
             </div>
           </div>
           <div class="expiry-info">
             <v-icon size="14" color="#6B7280">mdi-clock-outline</v-icon>
             <span>Valid until {{ formatDate(award.expiryDate) }}</span>
           </div>
         </div>
       </div>
       
       <div class="scratch-instructions">
         <v-icon size="16" color="#6B7280">mdi-gesture-tap</v-icon>
         <span>Click and drag to scratch</span>
       </div>
     </div>

     <!-- Revealed Coupon Section (after scratching) -->
     <div
       v-if="showCouponCode && couponRevealed"
       class="coupon-section revealed"
       @click.stop
     >
       <div class="coupon-header">
         <div class="coupon-icon">
           <v-icon color="#22C55E">mdi-ticket-confirmation</v-icon>
         </div>
         <span class="coupon-title">Coupon Code Revealed!</span>
       </div>
       <div class="coupon-code" @click="copyCouponCode">
         <span class="code-text">{{ award.couponCode }}</span>
         <v-icon size="16" :color="copied ? '#22C55E' : '#6B7280'">
           {{ copied ? 'mdi-check' : 'mdi-content-copy' }}
         </v-icon>
       </div>
     </div>

     <!-- Eligible Users Section -->
     <div v-if="users.length > 0 && profileType !== 'Volunteer'" class="eligible-users">
       <h4 class="section-title">Eligible Users</h4>
       <div class="users-grid">
         <div v-for="user in users.slice(0, 3)" :key="user._id" class="user-item">
           <div class="user-avatar">
             <editable-avatar
               :image="user.midThumbnail || user.thumbnail"
               :editable="false"
               :size="40"
             />
           </div>
           <div class="user-info">
             <span class="user-name">{{ user.first_name }} {{ user.last_name }}</span>
             <span class="user-hours">{{ formatMinutes(user.volunteered_hours) }} hrs</span>
           </div>
         </div>
         <div v-if="users.length > 3" class="more-users">
           +{{ users.length - 3 }} more
         </div>
       </div>
     </div>

     <!-- Action Section -->
     <div class="action-section">
      <v-btn
        v-if="showRedeemButton && new Date(award.expiryDate) > new Date()"
        class="redeem-btn"
        @click.stop="couponView(award)"
        elevation="0"
        block
      >
        <v-icon left size="18">mdi-gift</v-icon>
        Redeem Award
      </v-btn>
     </div>
   </div>

   <!-- Snackbar -->
   <v-snackbar v-model="snackbar" class="premium-snackbar">
     {{ snackMessage }}
   </v-snackbar>

   <!-- Dialogs -->
   <edit-awards
     :dialog="editDialog"
     :onClose="onCloseDialog"
     :item="award"
     v-if="editDialog"
   />
   <confirm-delete-dialog
     :deleteDialog="deleteDialog"
     :onClose="onCloseDialog"
     :onConfirm="onDeleteAward"
   />
   <couponsend-dialog
     :dialog="sendCouponDialog"
     :onClose="onCloseDialog"
     :couponWinners="couponWinners"
   />
   <coupon-show-dialog
     :dialog="couponShowDialog"
     :onClose="onCloseDialog"
     :showCouponcodes="showCouponcodes"
     @coupon-revealed="handleCouponRevealed"
   />
 </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import dateFormat from "dateformat";
import EditAwards from "./EditAwards.vue";
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import EditableAvatar from "../../components/EditableAvatar.vue";
import CouponsendDialog from "./CouponsendDialog.vue";
import CouponShowDialog from "./CouponShowDialog.vue";

export default {
 components: {
   EditAwards,
   ConfirmDeleteDialog,
   EditableAvatar,
   CouponsendDialog,
   CouponShowDialog
 },
 props: {
   award: Object,
   activeConversation: Object,
   hideButtons: {
     type: Boolean,
     default: false
   },
   onClicked: Function,
   eligibles: Array
 },
 data() {
   return {
     snackbar: false,
     snackMessage: null,
     text_collapsed: false,
     collapsed_text: "",
     code: "",
     coupon: false,
     editDialog: false,
     sendCouponDialog: false,
     couponShowDialog: false,
     deleteDialog: false,
     users: [],
     couponWinners: {},
     showCouponcodes: {},
     couponRevealed: false,
     myHours: 0,
     // Scratch card properties
     isScratching: false,
     scratchProgress: 0,
     showConfetti: false,
     copied: false,
     scratchPixels: []
   };
 },
 computed: {
   ...mapState("auth", {
     profile: "profile",
     profileType: "type"
   }),
   ...mapGetters({
     timezone: "usersetting/getTimeZone"
   }),
   isEligible() {
     return this.myHours >= parseInt(this.award.goal) * 60 && this.profileType === 'Volunteer';
   },
   isRedeemed() {
     return this.profileType === 'Volunteer' &&
            this.award.reedemedUsers &&
            this.award.reedemedUsers.some(
              coupon => coupon.redeemed_by === this.profile._id
            );
   },
   showCouponCode() {
     return this.profileType === 'Volunteer' &&
            this.award.reedemedUsers &&
            this.award.reedemedUsers.some(
              coupon => coupon.redeemed_by === this.profile._id
            );
   },
   showRedeemButton() {
     return this.profileType === 'Volunteer' &&
            !this.award.reedemedUsers?.some(
              coupon => coupon.code_used && coupon.redeemed_by === this.profile._id
            );
   },
   progressPercentage() {
     const goalMinutes = parseInt(this.award.goal) * 60;
     return Math.min((this.myHours / goalMinutes) * 100, 100);
   }
 },
 methods: {
   ...mapActions("awards", {
     deleteAward: "deleteAward",
     addChat: "chat/addChat",
     getVolunteerHours: "getVolunteerHours",
     getEligibles: "getEligibles",
     fetchAllAwards: "fetchAllAwards",
     initConversationsClient: "chat/initConversationsClient",
     updateConversation: "chat/updateConversation",
     sendMessageNotification: "chat/sendMessageNotification",
     conversationsClient: "chat/conversationsClient"
   }),

   // Scratch card methods
   initScratchCard() {
     this.$nextTick(() => {
       const canvas = this.$refs.scratchCanvas;
       if (!canvas) return;

       const rect = canvas.parentElement.getBoundingClientRect();
       canvas.width = rect.width;
       canvas.height = rect.height;

       const ctx = canvas.getContext('2d');
       
       // Create scratch surface
       const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
       gradient.addColorStop(0, '#C0C0C0');
       gradient.addColorStop(0.5, '#E5E5E5');
       gradient.addColorStop(1, '#A0A0A0');
       
       ctx.fillStyle = gradient;
       ctx.fillRect(0, 0, canvas.width, canvas.height);
       
       // Add scratch texture
       ctx.font = 'bold 16px Arial';
       ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
       ctx.textAlign = 'center';
       ctx.fillText('SCRATCH HERE', canvas.width / 2, canvas.height / 2 - 10);
       ctx.fillText('TO REVEAL', canvas.width / 2, canvas.height / 2 + 10);
       
       // Add sparkle effects
       for (let i = 0; i < 20; i++) {
         ctx.fillStyle = 'rgba(255, 215, 0, 0.6)';
         ctx.beginPath();
         ctx.arc(
           Math.random() * canvas.width,
           Math.random() * canvas.height,
           Math.random() * 2 + 1,
           0,
           2 * Math.PI
         );
         ctx.fill();
       }
     });
   },

   startScratch(e) {
     this.isScratching = true;
     this.scratch(e);
   },

   scratch(e) {
     if (!this.isScratching) return;

     const canvas = this.$refs.scratchCanvas;
     const ctx = canvas.getContext('2d');
     const rect = canvas.getBoundingClientRect();
     
     let x, y;
     if (e.type.includes('touch')) {
       x = e.touches[0].clientX - rect.left;
       y = e.touches[0].clientY - rect.top;
     } else {
       x = e.clientX - rect.left;
       y = e.clientY - rect.top;
     }

     ctx.globalCompositeOperation = 'destination-out';
     ctx.beginPath();
     ctx.arc(x, y, 20, 0, 2 * Math.PI);
     ctx.fill();

     // Calculate scratch progress
     this.calculateScratchProgress();
   },

   endScratch() {
     this.isScratching = false;
   },

   calculateScratchProgress() {
     const canvas = this.$refs.scratchCanvas;
     const ctx = canvas.getContext('2d');
     const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
     const pixels = imageData.data;
     
     let transparentPixels = 0;
     for (let i = 3; i < pixels.length; i += 4) {
       if (pixels[i] < 255) transparentPixels++;
     }
     
     const progress = (transparentPixels / (pixels.length / 4)) * 100;
     this.scratchProgress = progress;
     
     // Reveal coupon when 40% is scratched
     if (progress > 40 && !this.couponRevealed) {
       this.revealCoupon();
     }
   },

   revealCoupon() {
     this.couponRevealed = true;
     this.showConfetti = true;
     
     // Hide canvas with animation
     const canvas = this.$refs.scratchCanvas;
     if (canvas) {
       canvas.style.transition = 'opacity 0.5s ease-out';
       canvas.style.opacity = '0';
       setTimeout(() => {
         canvas.style.display = 'none';
       }, 500);
     }
     
     // Stop confetti after animation
     setTimeout(() => {
       this.showConfetti = false;
     }, 3000);
   },

   async copyCouponCode() {
     try {
       await navigator.clipboard.writeText(this.award.couponCode);
       this.copied = true;
       this.snackMessage = "Coupon code copied to clipboard!";
       this.snackbar = true;
       
       setTimeout(() => {
         this.copied = false;
         this.snackbar = false;
       }, 3000);
     } catch (err) {
       console.error("Failed to copy: ", err);
       this.snackMessage = "Failed to copy coupon code!";
       this.snackbar = true;
     }
   },

   goToDetails() {
     // Navigation logic here
   },

   couponView(award) {
     this.showCouponcodes = award;
     this.couponShowDialog = true;
   },

   handleCouponRevealed(couponRevealed) {
     this.couponRevealed = couponRevealed;
   },

   getCollapedText() {
     if (!this.award.description) {
       this.text_collapsed = false;
       return;
     }
     const full_text = this.award.description.trim();
     const textArray = full_text.split(/\r\n|\r|\n/);

     if (textArray.length > 3) {
       this.collapsed_text = textArray.slice(0, 3).join("\n");
       this.text_collapsed = true;
       return;
     }

     if (full_text.length > 270) {
       this.collapsed_text = full_text.substring(0, 270);
       this.text_collapsed = true;
       return;
     }

     this.collapsed_text = full_text;
     this.text_collapsed = false;
   },

   formatDate(date) {
     return dateFormat(this.convertTZ(date, this.timezone), "mm/dd/yyyy");
   },

   convertTZ(date, tzString) {
     if (!date) return "";
     return new Date(
       (typeof date === "string" ? new Date(date) : date).toLocaleString(
         "en-US",
         {
           timeZone: tzString ? tzString : "America/New_York"
         }
       )
     );
   },

   onEdit() {
     this.editDialog = true;
   },

   onDelete() {
     this.deleteDialog = true;
   },

   onCloseDialog() {
     this.deleteDialog = false;
     this.editDialog = false;
     this.sendCouponDialog = false;
     this.couponShowDialog = false;
   },

   onDeleteAward() {
     this.deleteAward({ _id: this.award._id }).catch(error => {
       console.log(error);
     });
   },

   formatMinutes(value) {
     var hrs = Math.floor(value / 60);
     var mins = Math.floor(value % 60);
     return `${hrs}:${mins.toString().padStart(2, '0')}`;
   }
 },

 mounted() {
   this.getCollapedText();
   this.handleCouponRevealed();
   
   // Initialize scratch card if coupon should be shown
   if (this.showCouponCode && !this.couponRevealed) {
     this.initScratchCard();
   }
 },

 watch: {
   showCouponCode: {
     handler(newVal) {
       if (newVal && !this.couponRevealed) {
         this.$nextTick(() => {
           this.initScratchCard();
         });
       }
     },
     immediate: true
   }
 }
};
</script>

<style scoped>
/* Previous styles remain the same... */
.award-card-wrapper {
 margin-bottom: 24px;
 cursor: pointer;
 transition: transform 0.2s ease;
}

.award-card-wrapper:hover {
 transform: translateY(-2px);
}

.award-card {
 background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
 border-radius: 20px;
 padding: 24px;
 position: relative;
 overflow: hidden;
 transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
 box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.award-card:hover {
 box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
 border-color: #3b82f6;
}

.award-card.eligible-glow {
 border-color: #22c55e;
 box-shadow: 0 4px 20px rgba(34, 197, 94, 0.15);
}

.award-card::before {
 content: '';
 position: absolute;
 top: 0;
 left: 0;
 right: 0;
 height: 4px;
 background: linear-gradient(90deg, #4f46e5, #10b981);
 border-radius: 20px 20px 0 0;
}

.status-badge {
 position: absolute;
 top: 16px;
 right: 16px;
 display: flex;
 align-items: center;
 gap: 6px;
 padding: 8px 12px;
 border-radius: 20px;
 font-size: 12px;
 font-weight: 600;
 z-index: 2;
}

.status-badge.eligible {
 background: linear-gradient(135deg, #22c55e, #16a34a);
 color: white;
}

.status-badge.redeemed {
 background: linear-gradient(135deg, #f59e0b, #d97706);
 color: white;
}

.award-header {
 display: flex;
 align-items: flex-start;
 gap: 16px;
 margin-bottom: 20px;
}

.award-icon-container {
 position: relative;
 display: flex;
 align-items: center;
 justify-content: center;
}

.award-icon-bg {
 width: 64px;
 height: 64px;
 background: linear-gradient(135deg, #dbe4ea, #d4e5f0);
 border-radius: 16px;
 display: flex;
 align-items: center;
 justify-content: center;
 position: relative;
 z-index: 2;
}

.pulse-ring {
 position: absolute;
 width: 64px;
 height: 64px;
 border: 2px solid #0191ff;
 border-radius: 16px;
 opacity: 0.3;
 animation: pulse 2s infinite;
}

@keyframes pulse {
 0% {
   transform: scale(1);
   opacity: 0.3;
 }
 50% {
   transform: scale(1.1);
   opacity: 0.1;
 }
 100% {
   transform: scale(1);
   opacity: 0.3;
 }
}

.award-info {
 flex: 1;
}

.award-title {
 font-size: 20px;
 font-weight: 700;
 color: #1f2937;
 margin: 0 0 8px 0;
 line-height: 1.3;
}

.award-meta {
 display: flex;
 flex-direction: column;
 gap: 6px;
}

.meta-item {
 display: flex;
 align-items: center;
 gap: 8px;
 font-size: 14px;
 color: #6b7280;
}

.action-buttons {
 display: flex;
 gap: 8px;
}

.action-btn {
 width: 40px !important;
 height: 40px !important;
 border-radius: 12px !important;
 transition: all 0.2s ease !important;
}

.edit-btn {
 background: linear-gradient(135deg, #3b82f6, #1d4ed8) !important;
 color: white !important;
}

.edit-btn:hover {
 transform: scale(1.05);
 box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.delete-btn {
 background: linear-gradient(135deg, #ef4444, #dc2626) !important;
 color: white !important;
}

.delete-btn:hover {
 transform: scale(1.05);
 box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

.description-section {
 margin-bottom: 20px;
}

.description-text {
 font-size: 15px;
 line-height: 1.6;
 color: #4b5563;
 margin: 0;
}

.read-more-btn {
 background: none;
 border: none;
 color: #3b82f6;
 font-weight: 600;
 cursor: pointer;
 font-size: 14px;
 margin-left: 4px;
 text-decoration: underline;
}

.read-more-btn:hover {
 color: #1d4ed8;
}

/* Scratch Card Styles */
.scratch-card-container {
 background: linear-gradient(135deg, #f8fafc, #e2e8f0);
 border: 2px solid #e5e7eb;
 border-radius: 20px;
 padding: 20px;
 margin-bottom: 20px;
 position: relative;
 overflow: hidden;
}

.scratch-card-header {
 display: flex;
 align-items: center;
 justify-content: center;
 gap: 12px;
 margin-bottom: 16px;
}

.scratch-icon {
 width: 40px;
 height: 40px;
 background: linear-gradient(135deg, #fbbf24, #f59e0b);
 border-radius: 50%;
 display: flex;
 align-items: center;
 justify-content: center;
 animation: sparkle 2s infinite;
}

@keyframes sparkle {
 0%, 100% { transform: scale(1) rotate(0deg); }
 25% { transform: scale(1.1) rotate(5deg); }
 75% { transform: scale(1.1) rotate(-5deg); }
}

.scratch-title {
 font-size: 16px;
 font-weight: 600;
 color: #374151;
 text-align: center;
}

.scratch-card {
 position: relative;
 height: 200px;
 border-radius: 16px;
 overflow: hidden;
 background: linear-gradient(135deg, #fef3c7, #fbbf24);
 margin-bottom: 12px;
}

.scratch-canvas {
 position: absolute;
 top: 0;
 left: 0;
 width: 100%;
 height: 100%;
 cursor: crosshair;
 z-index: 2;
 border-radius: 16px;
}

.coupon-content {
 position: absolute;
 top: 0;
 left: 0;
 width: 100%;
 height: 100%;
 display: flex;
 flex-direction: column;
 justify-content: center;
 align-items: center;
 padding: 20px;
 z-index: 1;
}

.confetti-animation {
 position: absolute;
 top: 0;
 left: 0;
 width: 100%;
 height: 100%;
 pointer-events: none;
 z-index: 3;
}

.confetti {
 position: absolute;
 width: 6px;
 height: 6px;
 background: #fbbf24;
 animation: confetti-fall 3s linear infinite;
}

.confetti:nth-child(odd) {
 background: #22c55e;
 animation-delay: -0.5s;
}

.confetti:nth-child(3n) {
 background: #3b82f6;
 animation-delay: -1s;
}

.confetti:nth-child(4n) {
 background: #ef4444;
 animation-delay: -1.5s;
}

@keyframes confetti-fall {
 0% {
   transform: translateY(-100px) rotate(0deg);
   opacity: 1;
 }
 100% {
   transform: translateY(300px) rotate(360deg);
   opacity: 0;
 }
}

.confetti:nth-child(1) { left: 10%; }
.confetti:nth-child(2) { left: 20%; }
.confetti:nth-child(3) { left: 30%; }
.confetti:nth-child(4) { left: 40%; }
.confetti:nth-child(5) { left: 50%; }
.confetti:nth-child(6) { left: 60%; }
.confetti:nth-child(7) { left: 70%; }
.confetti:nth-child(8) { left: 80%; }
.confetti:nth-child(9) { left: 90%; }

.coupon-celebration {
 text-align: center;
 margin-bottom: 16px;
}

.congratulations {
 font-size: 18px;
 font-weight: 700;
 color: #374151;
 margin: 8px 0;
}

.discount-badge {
 background: linear-gradient(135deg, #ef4444, #dc2626);
 color: white;
 font-size: 24px;
 font-weight: 800;
 padding: 8px 16px;
 border-radius: 12px;
 display: inline-block;
 margin-bottom: 4px;
 text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.coupon-description {
 font-size: 14px;
 color: #6b7280;
 margin: 0;
}

.coupon-code-reveal {
 background: rgba(255, 255, 255, 0.9);
 border: 2px dashed #fbbf24;
 border-radius: 12px;
 padding: 12px 16px;
 cursor: pointer;
 transition: all 0.2s ease;
 min-width: 200px;
}

.coupon-code-reveal:hover {
 background: white;
 transform: scale(1.02);
 box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
}

.code-label {
 font-size: 12px;
 color: #6b7280;
 display: block;
 margin-bottom: 4px;
}

.code-display {
 display: flex;
 align-items: center;
 justify-content: space-between;
 gap: 8px;
}

.code-text {
 font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
 font-size: 16px;
 font-weight: 700;
 color: #374151;
 letter-spacing: 1px;
}

.expiry-info {
 display: flex;
 align-items: center;
 gap: 6px;
 font-size: 12px;
 color: #6b7280;
 margin-top: 12px;
}

.scratch-instructions {
 display: flex;
 align-items: center;
 justify-content: center;
 gap: 8px;
 font-size: 14px;
 color: #6b7280;
 font-style: italic;
}

/* Revealed Coupon Section */
.coupon-section {
 background: linear-gradient(135deg, #dcfce7, #bbf7d0);
 border: 1px solid #22c55e;
 border-radius: 16px;
 padding: 16px;
 margin-bottom: 20px;
}

.coupon-section.revealed {
 animation: slideIn 0.5s ease-out;
}

@keyframes slideIn {
 from {
   opacity: 0;
   transform: translateY(20px);
 }
 to {
   opacity: 1;
   transform: translateY(0);
 }
}

.coupon-header {
 display: flex;
 align-items: center;
 gap: 8px;
 margin-bottom: 12px;
}

.coupon-icon {
 width: 32px;
 height: 32px;
 background: #22c55e;
 border-radius: 8px;
 display: flex;
 align-items: center;
 justify-content: center;
}

.coupon-title {
 font-size: 14px;
 font-weight: 600;
 color: #166534;
}

.coupon-code {
 background: white;
 border: 2px dashed #22c55e;
 border-radius: 12px;
 padding: 12px 16px;
 display: flex;
 align-items: center;
 justify-content: space-between;
 cursor: pointer;
 transition: all 0.2s ease;
}

.coupon-code:hover {
 background: #f0fdf4;
 transform: scale(1.02);
}

.coupon-code .code-text {
 font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
 font-size: 16px;
 font-weight: 700;
 color: #166534;
 letter-spacing: 1px;
}

.eligible-users {
 margin-bottom: 20px;
}

.section-title {
 font-size: 16px;
 font-weight: 600;
 color: #374151;
 margin: 0 0 12px 0;
}

.users-grid {
 display: flex;
 flex-wrap: wrap;
 gap: 12px;
 align-items: center;
}

.user-item {
 display: flex;
 align-items: center;
 gap: 12px;
 background: #f8fafc;
 border-radius: 12px;
 padding: 12px;
 min-width: 160px;
}

.user-avatar {
 flex-shrink: 0;
}

.user-info {
 display: flex;
 flex-direction: column;
}

.user-name {
 font-size: 14px;
 font-weight: 600;
 color: #374151;
 line-height: 1.2;
}

.user-hours {
 font-size: 12px;
 color: #6b7280;
}

.more-users {
 background: #e2e8f0;
 color: #64748b;
 border-radius: 12px;
 padding: 12px 16px;
 font-size: 14px;
 font-weight: 500;
}

.action-section {
 margin-top: 20px;
}

.redeem-btn {
 background: linear-gradient(135deg, #0a5b8a, #1DA1F2) !important;
 color: white !important;
 font-weight: 600 !important;
 font-size: 16px !important;
 height: 48px !important;
 border-radius: 16px !important;
 text-transform: none !important;
 transition: all 0.3s ease !important;
}

.redeem-btn:hover {
 transform: translateY(-2px);
 box-shadow: 0 8px 25px rgba(139, 92, 246, 0.3) !important;
}

.premium-snackbar {
 border-radius: 12px !important;
}

/* Responsive Design */
@media (max-width: 768px) {
 .award-card {
   padding: 20px;
 }
 
 .award-header {
   flex-direction: column;
   gap: 12px;
 }
 
 .action-buttons {
   align-self: flex-end;
 }
 
 .users-grid {
   flex-direction: column;
 }
 
 .user-item {
   width: 100%;
 }
 
 .scratch-card {
   height: 180px;
 }
 
 .discount-badge {
   font-size: 20px;
 }
 
 .coupon-code-reveal {
   min-width: 180px;
 }
}

/* Touch device optimizations */
@media (hover: none) and (pointer: coarse) {
 .scratch-canvas {
   cursor: grab;
 }
 
 .scratch-canvas:active {
   cursor: grabbing;
 }
}

/* Dark mode support */
/* @media (prefers-color-scheme: dark) {
 .award-card {
   background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
   color: #f9fafb;
 }
 
 .award-title {
   color: #f9fafb;
 }
 
 .description-text {
   color: #d1d5db;
 }
 
 .scratch-card-container {
   background: linear-gradient(135deg, #374151, #1f2937);
   border-color: #4b5563;
 }
 
 .scratch-title {
   color: #f9fafb;
 }
} */

</style>